import * as ko from "knockout";

import * as shared from "./signInShared";
import { SignInPageMain, SignInPageMainActivationSettings, SignInPageMainActivationInfo } from "./signInPageMain";
import { SignInPageForgot } from "./signInPageForgot";
import { SignInPageMessage } from "./signInPageMessage";
import { SignInPageChangePassword } from "./signInPageChangePassword";
import { pli } from "../core/pli";


/**
 * Describes shape of page classes.
 *
 * A page is a well-defined area of user-facing functionality (e.g. main page
 * with username + password, forgotten username/password page with email, etc).
 * One page can transition to any other page (using animation).
 */
interface PageType {
    /**
     * Common constructor for page classes.
     * Requires one parameter, link to the page owner
     * ("SignInViewModel" implements "AnimatedPagesCollection" :-) ).
     */
    new (owner: shared.SignInPagesCollection, pageId: shared.SignInPageId): any;
}


/**
 * Metadata used to instantiate sign-in page as a Knockout component.
 */
interface PageDescriptor {
    /**
     * Type of page class to instantiate as a viewmodel for this component.
     */
    pageType?: PageType;

    /**
     * Name of the component (used in Knockout registration).
     * Also ID of the DOM element used as a template for this component.
     */
    componentName: string;
}


/**
 * Registry of known pages (pages that can be instantiated as Knockout components when needed).
 */
interface PageRegistry {
    /**
     * Gets a descriptor for the specified page.
     */
    [pageId: number]: PageDescriptor;
}


/**
 * Viewmodel for the sign-in form (contains multiple pages).
 *
 * A page is a well-defined area of user-facing functionality (e.g. main page
 * with username + password, forgotten username/password page with email, etc).
 * One page can transition to any other page (using animation).
 */
export class SignInViewModel implements shared.SignInPagesCollection {

    /**
     * Create a new instance of sign-in viewmodel, creating descriptors for all supported pages.
     */
    constructor() {
        // define supported pages
        const pageRegistry: PageRegistry = this._pageRegistry = {};
        pageRegistry[shared.SignInPageId.None] = { componentName: "signInEmpty" };
        pageRegistry[shared.SignInPageId.Main] = { componentName: "signInPageMain", pageType: SignInPageMain };
        pageRegistry[shared.SignInPageId.Forgot] = { componentName: "signInPageForgot", pageType: SignInPageForgot };
        pageRegistry[shared.SignInPageId.LockedOut] = { componentName: "signInPageLockedOut", pageType: SignInPageMessage };
        pageRegistry[shared.SignInPageId.ChangePassword] = { componentName: "signInPageChangePassword", pageType: SignInPageChangePassword };

        // register components in Knockout
        for (const pageId in pageRegistry) {
            if (pageRegistry.hasOwnProperty(pageId)) {
                const pageDescriptor = pageRegistry[pageId];
                if (ko.components.isRegistered(pageDescriptor.componentName)) { // remove old registration (if any)
                    ko.components.unregister(pageDescriptor.componentName);
                }
                ko.components.register(
                    pageDescriptor.componentName,
                    pageDescriptor.pageType
                        ? { viewModel: this._createPageGetter(pageId as any), template: { element: pageDescriptor.componentName } }
                        : { template: " " }
                );
            }
        }
    }


    // -------------------------------------------------------------------------
    // Viewmodel properties
    // -------------------------------------------------------------------------

    /** Version of this web application (server build version). */
    appVersion: string = pli.config.appVersion;

    /** Tracks currently selected page. */
    currentPageId: ko.Observable<shared.SignInPageId> = ko.observable(0);


    // -------------------------------------------------------------------------
    // Viewmodel actions
    // -------------------------------------------------------------------------

    /**
     * Activates main page of this viewmodel, getting it ready for user interaction.
     */
    activate(settings?: SignInPageMainActivationSettings): SignInPageMainActivationInfo {
        const mainPage = this.getPage<SignInPageMain>(shared.SignInPageId.Main);

        if (!settings) { settings = {}; }
        if (settings.focusDelay === undefined) { settings.focusDelay = 750; } // sign-in form opening animation duration

        return mainPage.activate(settings);
    }

    /**
     * Gets viewmodel for the specified page.
     */
    getPage<T>(pageId: shared.SignInPageId, skipCreation?: boolean): T {
        const pages = this._pages();
        const newPages = (pages ? pages : []);
        const page = newPages[pageId];
        const newPage = (page || skipCreation ? page : this._createPage(pageId, this));

        if (page !== newPage) {
            newPages[pageId] = newPage;
            if (newPages !== pages) {
                this._pages(newPages);
            } else {
                this._pages.valueHasMutated();
            }
        }

        return newPage as T;
    }

    /**
     * Convert page ID to component name.
     *
     * If the specified page has not been instantiated yet, empty component
     * will be used instead (with no viewmodel). Intended to be used in
     * "component" binding, e.g. data-bind='component: pageAsComponent(1)'".
     */
    pageAsComponent(pageId: shared.SignInPageId): string {
        const page = this.getPage<any>(pageId, /* skipCreation*/ true); // will be null if viewmodel for the specified page has not been created yet
        const componentPageId = (page ? pageId : shared.SignInPageId.None);
        return this._pageRegistry[componentPageId].componentName;
    }

    /**
     * Clear all currently instantiated pages + set current page to "None".
     *
     * Typically called when sign-in-form close animation finishes.
     * When pages are cleared, all Knockout components bound to them will
     * be released as well (DOM nodes removed, viewmodels disposed).
     */
    reset(): void {
        this.currentPageId(shared.SignInPageId.None);
        this._pages(null);
    }

    /**
     * Forward submit handler to the current page (if any).
     */
    submit(): void {
        const currentPageId = Math.abs(this.currentPageId());
        const currentPage = this.getPage<any>(currentPageId, /* skipCreation */ true);
        if (currentPage && currentPage.submit) {
            return currentPage.submit();
        }
    }


    // -------------------------------------------------------------------------
    // Viewmodel actions
    // -------------------------------------------------------------------------

    /**
     * Instantiate viewmodel for the page with the specified ID.
     */
    private _createPage(pageId: shared.SignInPageId, owner: any): any {
        const description = this._pageRegistry[pageId];
        if (!description) { throw new Error("Unknown sign-in page " + pageId); }
        return new description.pageType(owner, pageId);
    }

    /**
     * Create a function that will create the specified page in the future.
     *
     * This is a separate method because we need to introduce closure on
     * the current "pageId" value in a loop.
     */
    private _createPageGetter(pageId: shared.SignInPageId): (params?: any) => any {
        return () => this.getPage(pageId);
    }

    /** Registry of known pages (pages that can be instantiated). */
    private _pageRegistry: { [pageId: number]: PageDescriptor }; // readonly, initialized in constructor

    /** Collection of currently instantiated pages. */
    private _pages: ko.Observable<any[]> = ko.observable(null); // readonly

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms-signin/signInViewModel.ts